import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const MAX_MEMBRANE_MODES = 8;
export const BODY_COUNT = 4;
export const MIN_FUNDAMENTAL_HZ = 20;
export const MAX_FUNDAMENTAL_HZ = 2_000;
export const MIN_DECAY_SECONDS = 0.02;
export const MAX_DECAY_SECONDS = 10;
export const MIN_PITCH_DECAY_MS = 5;
export const MAX_PITCH_DECAY_MS = 1_000;
export const MIN_ENERGY_LIMIT = 0.001;
export const MAX_ENERGY_LIMIT = 4;

export type MembraneBody = 'kick' | 'snare' | 'tom' | 'hand-drum';

export interface MembraneDrumOptions {
  readonly sampleRate: number;
  readonly body?: MembraneBody;
  readonly fundamentalHz?: number;
  readonly decaySeconds?: number;
  readonly pitchSweep?: number;
  readonly pitchDecayMs?: number;
  readonly damping?: number;
  readonly excitation?: number;
  readonly outputGain?: number;
  /** Maximum squared state magnitude per mode. */
  readonly energyLimit?: number;
}

interface BodyProfile {
  readonly ratios: readonly number[];
  readonly gains: readonly number[];
  readonly decays: readonly number[];
  readonly excitations: readonly number[];
}

// Ratios start from ideal circular-membrane modes and are deliberately weighted
// into four useful excitation/decay profiles. These are original presets rather
// than measurements or copied instrument data.
const BODY_PROFILES: readonly BodyProfile[] = Object.freeze([
  Object.freeze({
    ratios: Object.freeze([1, 1.593, 2.136]),
    gains: Object.freeze([1, 0.12, 0.045]),
    decays: Object.freeze([1, 0.32, 0.18]),
    excitations: Object.freeze([1, 0.45, 0.2]),
  }),
  Object.freeze({
    ratios: Object.freeze([1, 1.593, 2.136, 2.296, 2.653, 2.918, 3.156, 3.5]),
    gains: Object.freeze([0.38, 0.42, 0.5, 0.45, 0.38, 0.31, 0.25, 0.2]),
    decays: Object.freeze([0.8, 0.6, 0.42, 0.36, 0.3, 0.24, 0.2, 0.16]),
    excitations: Object.freeze([0.65, 0.82, 1, 0.95, 0.88, 0.78, 0.68, 0.58]),
  }),
  Object.freeze({
    ratios: Object.freeze([1, 1.593, 2.136, 2.296, 2.653]),
    gains: Object.freeze([0.85, 0.32, 0.2, 0.13, 0.08]),
    decays: Object.freeze([1, 0.68, 0.45, 0.3, 0.2]),
    excitations: Object.freeze([1, 0.75, 0.55, 0.38, 0.25]),
  }),
  Object.freeze({
    ratios: Object.freeze([1, 1.593, 2.136, 2.296, 2.653, 2.918, 3.156]),
    gains: Object.freeze([0.68, 0.46, 0.31, 0.25, 0.18, 0.13, 0.09]),
    decays: Object.freeze([0.8, 0.72, 0.5, 0.38, 0.28, 0.2, 0.14]),
    excitations: Object.freeze([0.8, 1, 0.78, 0.65, 0.5, 0.38, 0.28]),
  }),
]);

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

const assertIntegerRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be an integer in [${minimum}, ${maximum}]`);
  }
};

const bodyIndex = (body: MembraneBody): number => {
  if (body === 'kick') return 0;
  if (body === 'snare') return 1;
  if (body === 'tom') return 2;
  if (body === 'hand-drum') return 3;
  throw new RangeError(`unknown body: ${String(body)}`);
};

/**
 * A bounded modal circular-membrane model intended for kick, snare, tom and
 * hand-drum bodies. Construction and parameter changes may update coefficient
 * storage; processInto performs no explicit allocation and writes only into the
 * caller-owned output buffer.
 */
export class MembraneDrum {
  readonly sampleRate: number;

  private body: number;
  private fundamentalHz: number;
  private decaySeconds: number;
  private pitchSweep: number;
  private pitchDecayMs: number;
  private dampingAmount: number;
  private excitationAmount: number;
  private outputGain: number;
  private energyLimit: number;
  private pitchEnvelope = 0;
  private pitchEnvelopeMultiplier = 0;
  private modeCount = 0;

  private readonly ratios = new Float64Array(MAX_MEMBRANE_MODES);
  private readonly gains = new Float64Array(MAX_MEMBRANE_MODES);
  private readonly excitationGains = new Float64Array(MAX_MEMBRANE_MODES);
  private readonly poleRadii = new Float64Array(MAX_MEMBRANE_MODES);
  private readonly real = new Float64Array(MAX_MEMBRANE_MODES);
  private readonly imaginary = new Float64Array(MAX_MEMBRANE_MODES);

  constructor(options: MembraneDrumOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.body = bodyIndex(options.body ?? 'kick');
    this.fundamentalHz = options.fundamentalHz ?? 80;
    this.decaySeconds = options.decaySeconds ?? 0.7;
    this.pitchSweep = options.pitchSweep ?? 0.65;
    this.pitchDecayMs = options.pitchDecayMs ?? 70;
    this.dampingAmount = options.damping ?? 1;
    this.excitationAmount = options.excitation ?? 1;
    this.outputGain = options.outputGain ?? 0.8;
    this.energyLimit = options.energyLimit ?? 1;

    assertRange('fundamentalHz', this.fundamentalHz, MIN_FUNDAMENTAL_HZ, MAX_FUNDAMENTAL_HZ);
    assertRange('decaySeconds', this.decaySeconds, MIN_DECAY_SECONDS, MAX_DECAY_SECONDS);
    assertRange('pitchSweep', this.pitchSweep, 0, 3);
    assertRange('pitchDecayMs', this.pitchDecayMs, MIN_PITCH_DECAY_MS, MAX_PITCH_DECAY_MS);
    assertRange('damping', this.dampingAmount, 0, 4);
    assertRange('excitation', this.excitationAmount, 0, 1);
    assertRange('outputGain', this.outputGain, 0, 2);
    assertRange('energyLimit', this.energyLimit, MIN_ENERGY_LIMIT, MAX_ENERGY_LIMIT);
    this.loadBody();
    this.updateCoefficients();
  }

  reset(): void {
    this.real.fill(0);
    this.imaginary.fill(0);
    this.pitchEnvelope = 0;
  }

  setParameter(id: string, value: number): void {
    if (id === 'body') {
      assertIntegerRange(id, value, 0, BODY_COUNT - 1);
      this.body = value;
      this.loadBody();
      this.updateCoefficients();
      return;
    }
    if (id === 'fundamental-hz') {
      assertRange(id, value, MIN_FUNDAMENTAL_HZ, MAX_FUNDAMENTAL_HZ);
      this.fundamentalHz = value;
      return;
    }
    if (id === 'decay-seconds') {
      assertRange(id, value, MIN_DECAY_SECONDS, MAX_DECAY_SECONDS);
      this.decaySeconds = value;
      this.updateCoefficients();
      return;
    }
    if (id === 'pitch-sweep') {
      assertRange(id, value, 0, 3);
      this.pitchSweep = value;
      return;
    }
    if (id === 'pitch-decay-ms') {
      assertRange(id, value, MIN_PITCH_DECAY_MS, MAX_PITCH_DECAY_MS);
      this.pitchDecayMs = value;
      this.updateCoefficients();
      return;
    }
    if (id === 'damping') {
      assertRange(id, value, 0, 4);
      this.dampingAmount = value;
      this.updateCoefficients();
      return;
    }
    if (id === 'excitation') {
      assertRange(id, value, 0, 1);
      this.excitationAmount = value;
      return;
    }
    if (id === 'output-gain') {
      assertRange(id, value, 0, 2);
      this.outputGain = value;
      return;
    }
    if (id === 'energy-limit') {
      assertRange(id, value, MIN_ENERGY_LIMIT, MAX_ENERGY_LIMIT);
      this.energyLimit = value;
      return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(
    excitation: Float32Array,
    output: Float32Array,
    energy?: Float32Array,
    frames = output.length,
  ): void {
    if (!Number.isInteger(frames) || frames < 0 || excitation.length < frames || output.length < frames || (energy !== undefined && energy.length < frames)) {
      throw new RangeError('buffers are too short for frames');
    }

    for (let frame = 0; frame < frames; frame += 1) {
      if (!Number.isFinite(excitation[frame]!)) throw new RangeError('excitation samples must be finite');
      const energyAmount = energy === undefined ? 1 : energy[frame]!;
      if (!Number.isFinite(energyAmount) || energyAmount < 0 || energyAmount > 1) {
        throw new RangeError('energy samples must be in [0, 1]');
      }
    }

    const sampleRate = this.sampleRate;
    const maximumFrequency = sampleRate * 0.49;
    const fundamentalHz = this.fundamentalHz;
    const pitchSweep = this.pitchSweep;
    const envelopeMultiplier = this.pitchEnvelopeMultiplier;
    const excitationAmount = this.excitationAmount;
    const outputGain = this.outputGain;
    const stateEnergyLimit = this.energyLimit;
    const modeCount = this.modeCount;
    let pitchEnvelope = this.pitchEnvelope;

    for (let frame = 0; frame < frames; frame += 1) {
      const inputSample = excitation[frame]!;
      const energyAmount = energy === undefined ? 1 : energy[frame]!;

      const boundedInput = Math.max(-1, Math.min(1, inputSample)) * energyAmount * excitationAmount;
      const hitEnergy = Math.abs(boundedInput);
      if (hitEnergy > pitchEnvelope) pitchEnvelope = hitEnergy;
      const pitchRatio = 1 + pitchSweep * pitchEnvelope * pitchEnvelope;
      let mixed = 0;

      for (let mode = 0; mode < modeCount; mode += 1) {
        const frequency = Math.min(maximumFrequency, fundamentalHz * this.ratios[mode]! * pitchRatio);
        const phase = 2 * Math.PI * frequency / sampleRate;
        const cosine = Math.cos(phase);
        const sine = Math.sin(phase);
        const previousReal = this.real[mode]!;
        const previousImaginary = this.imaginary[mode]!;
        const radius = this.poleRadii[mode]!;
        let nextReal = radius * (cosine * previousReal - sine * previousImaginary)
          + boundedInput * this.excitationGains[mode]!;
        let nextImaginary = radius * (sine * previousReal + cosine * previousImaginary);
        const stateEnergy = nextReal * nextReal + nextImaginary * nextImaginary;
        if (stateEnergy > stateEnergyLimit) {
          const normalization = Math.sqrt(stateEnergyLimit / stateEnergy);
          nextReal *= normalization;
          nextImaginary *= normalization;
        }
        if (!Number.isFinite(nextReal) || !Number.isFinite(nextImaginary)) {
          nextReal = 0;
          nextImaginary = 0;
        }
        this.real[mode] = Math.abs(nextReal) < 1e-30 ? 0 : nextReal;
        this.imaginary[mode] = Math.abs(nextImaginary) < 1e-30 ? 0 : nextImaginary;
        mixed += nextReal * this.gains[mode]!;
      }

      output[frame] = Math.max(-1, Math.min(1, mixed * outputGain));
      pitchEnvelope *= envelopeMultiplier;
      if (pitchEnvelope < 1e-12) pitchEnvelope = 0;
    }
    this.pitchEnvelope = pitchEnvelope;
  }

  private loadBody(): void {
    const profile = BODY_PROFILES[this.body]!;
    this.modeCount = profile.ratios.length;
    this.ratios.fill(0);
    this.gains.fill(0);
    this.excitationGains.fill(0);
    for (let mode = 0; mode < this.modeCount; mode += 1) {
      this.ratios[mode] = profile.ratios[mode]!;
      this.gains[mode] = profile.gains[mode]!;
      this.excitationGains[mode] = profile.excitations[mode]!;
    }
  }

  private updateCoefficients(): void {
    const profile = BODY_PROFILES[this.body]!;
    for (let mode = 0; mode < this.modeCount; mode += 1) {
      const highModeLoss = 1 + this.dampingAmount * Math.pow(this.ratios[mode]! - 1, 1.35);
      const modeDecay = Math.max(MIN_DECAY_SECONDS, this.decaySeconds * profile.decays[mode]! / highModeLoss);
      this.poleRadii[mode] = Math.exp(-1 / (modeDecay * this.sampleRate));
    }
    this.pitchEnvelopeMultiplier = Math.exp(-1 / (this.pitchDecayMs * 0.001 * this.sampleRate));
  }
}

class MembraneDrumModuleInstance implements DspModuleInstance {
  private readonly drum: MembraneDrum;

  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.drum = new MembraneDrum({ sampleRate });
  }

  reset(): void { this.drum.reset(); }
  setParameter(id: string, value: number): void { this.drum.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds the configured maximum block size');
    const excitation = block.inputs.excitation;
    const output = block.outputs.output;
    if (!excitation || excitation.length < block.frames) throw new RangeError('excitation buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('output buffer is missing or too short');
    const energy = block.controls.energy;
    if (energy && energy.length < block.frames) throw new RangeError('energy buffer is too short');
    this.drum.processInto(excitation, output, energy, block.frames);
  }
}

export const mod009 = defineDspModule({
  descriptor: {
    id: 'mir.mod-009',
    name: 'Membrane drum resonator',
    version: '0.1.0',
    inputs: [
      { id: 'excitation', kind: 'audio', channels: 1 },
      { id: 'energy', kind: 'control', channels: 1 },
    ],
    outputs: [{ id: 'output', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'body', defaultValue: 0, minValue: 0, maxValue: BODY_COUNT - 1, unit: 'index' },
      { id: 'fundamental-hz', defaultValue: 80, minValue: MIN_FUNDAMENTAL_HZ, maxValue: MAX_FUNDAMENTAL_HZ, unit: 'Hz' },
      { id: 'decay-seconds', defaultValue: 0.7, minValue: MIN_DECAY_SECONDS, maxValue: MAX_DECAY_SECONDS, unit: 's' },
      { id: 'pitch-sweep', defaultValue: 0.65, minValue: 0, maxValue: 3, unit: 'ratio' },
      { id: 'pitch-decay-ms', defaultValue: 70, minValue: MIN_PITCH_DECAY_MS, maxValue: MAX_PITCH_DECAY_MS, unit: 'ms' },
      { id: 'damping', defaultValue: 1, minValue: 0, maxValue: 4, unit: 'ratio' },
      { id: 'excitation', defaultValue: 1, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'output-gain', defaultValue: 0.8, minValue: 0, maxValue: 2, unit: 'linear' },
      { id: 'energy-limit', defaultValue: 1, minValue: MIN_ENERGY_LIMIT, maxValue: MAX_ENERGY_LIMIT, unit: 'power' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) throw new RangeError('invalid DSP instance context');
    return new MembraneDrumModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
